import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "./button";

type PaginationControlsProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  hideOnSinglePage?: boolean;
};

type PaginationItem = number | "...";

const PaginationControls = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  hideOnSinglePage = false,
}: PaginationControlsProps) => {
  if (totalPages <= 0 || (hideOnSinglePage && totalPages <= 1)) return null;

  const buildPageItems = (): PaginationItem[] => {
    const safeTotal = Math.max(1, totalPages);
    if (safeTotal <= 6) {
      return Array.from({ length: safeTotal }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", currentPage, "...", totalPages];
  };

  const pageItems = buildPageItems();

  const goToPrevious = () => {
    if (currentPage <= 1) return;
    onPageChange(currentPage - 1);
  };

  const goToNext = () => {
    if (currentPage >= totalPages) return;
    onPageChange(currentPage + 1);
  };

  return (
    <div className={className ?? "mt-8 flex items-center gap-2"}>
      <Button
        variant="outline"
        size="sm"
        className="aku-body-small-medium"
        onClick={goToPrevious}
        disabled={currentPage <= 1}
      >
        <ArrowLeft size={16} />
        Previous
      </Button>

      {pageItems.map((item, index) =>
        item === "..." ? (
          <span
            key={`ellipsis-${index}`}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E4E7F4] text-sm font-medium text-gray-30"
          >
            ...
          </span>
        ) : (
          <Button
            key={item}
            onClick={() => onPageChange(item)}
            variant="outline"
            size="sm"
            className={
              item === currentPage
                ? "h-9 w-9 px-0 text-sm font-medium text-gray-30"
                : "h-9 w-9 px-0 text-sm font-medium text-gray-20"
            }
            aria-current={item === currentPage ? "page" : undefined}
          >
            {item}
          </Button>
        ),
      )}

      <Button
        variant="outline"
        size="sm"
        className="aku-body-small-medium"
        onClick={goToNext}
        disabled={currentPage >= totalPages}
      >
        Next
        <ArrowRight size={16} />
      </Button>
    </div>
  );
};

export { PaginationControls };
